import type { ReactElement } from "react";
import { startTransition, useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";
import { useDispatch } from "react-redux";

import { showErrorToast, showSuccessToast } from "app/actions/toast_actions";
import { translate } from "app/filters/i18nFilter";
import { ALL_TOPICS_NAME } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import {
    DEFAULT_NEW_TOPIC_NAME,
    NEW_PROMPT_ID_SUFFIX,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/consts";
import { useCancelChangesModal } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/hooks/useCancelChangesModal";
import { useEmptyTopicsModal } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/hooks/useEmptyTopicsModal";
import { PromptsEditModal } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/PromptsEditModal";
import { allTrackers } from "app/services/track/track";

import { getMaxPrompts } from "../../../shared/LLMTrackerUtils";

import { useCSVUpload } from "./hooks/useCSVUpload";
import { useGeneratePrompts } from "./hooks/useGeneratePrompts";
import type { ICSVPromptData, IModifiablePrompt, IModifiableTopic, IGenerationProps } from "./types";

interface IPromptsEditModalHook {
    isOpen: boolean;
    openModal: () => void;
    modal: ReactElement;
}

interface IPromptsEditModalProps {
    data: IModifiableTopic[];
    onSave: (data: IModifiableTopic[]) => void;
    saveText: string;
    onManageLaterClick?: () => void;
    generationProps?: IGenerationProps;
    isLoading?: boolean;
    error?: boolean;
}

export const usePromptsEditModal = ({
    data,
    onSave,
    saveText,
    onManageLaterClick,
    generationProps,
    isLoading,
    error,
}: IPromptsEditModalProps): IPromptsEditModalHook => {
    const dispatch = useDispatch();
    const canGeneratePrompts = !!generationProps;
    const [isOpen, setOpen] = useState<boolean>(false);
    const [draftData, setDraftData] = useState<IModifiableTopic[]>([]);
    const hasChanges = useMemo<boolean>(() => {
        if (onManageLaterClick) {
            return true;
        }

        const stripTimestamps = (topics: IModifiableTopic[]) =>
            topics?.map(({ prompts, ...topic }) => ({
                ...topic,
                prompts: prompts.map(({ timestamp, ...prompt }) => prompt),
            }));

        const actualTopics = draftData?.filter(({ name }) => name !== ALL_TOPICS_NAME);
        return JSON.stringify(stripTimestamps(actualTopics)) !== JSON.stringify(stripTimestamps(data));
    }, [draftData, data, onManageLaterClick]);

    const emptyTopics = useMemo<string[]>(
        () =>
            draftData
                ?.filter(({ topicId }) => topicId !== ALL_TOPICS_NAME)
                .reduce((acc, { name, prompts }) => {
                    const isEmpty = !prompts.length;
                    return isEmpty ? [...acc, name] : acc;
                }, [] as string[]) ?? [],
        [draftData],
    );

    const { mutateAsync: generatePrompts, isPending: isGenerating } = useGeneratePrompts();
    const isSaveEnabled = useMemo<boolean>(() => {
        if (!hasChanges) {
            return false;
        }

        const promptsCount = draftData
            ?.filter(({ topicId }) => topicId !== ALL_TOPICS_NAME)
            .reduce((acc, { prompts }) => acc + prompts.length, 0);

        return promptsCount > 0 && promptsCount <= getMaxPrompts();
    }, [draftData, hasChanges]);

    const rebuildWithAllTopics = (actualTopics: IModifiableTopic[]): IModifiableTopic[] => [
        {
            topicId: ALL_TOPICS_NAME,
            name: ALL_TOPICS_NAME,
            prompts: actualTopics.flatMap(({ prompts }) => prompts),
        },
        ...actualTopics,
    ];

    useEffect(() => {
        if (data) {
            setDraftData(rebuildWithAllTopics(data));
        }
    }, [data, isOpen]);

    const openModal = (): void => {
        allTrackers.dispatchTrackEvent("Edit Campaign", "click", "edit prompts/open");
        setOpen(true);
    };

    const closeModal = (): void => {
        setOpen(false);
        allTrackers.dispatchTrackEvent("Edit Campaign", "click", "edit prompts/cancel");
    };

    const discardChangesAndClose = (): void => {
        setDraftData([]);
        closeModal();
    };

    const onDeletePrompts = (promptsToDelete: IModifiablePrompt[]): void => {
        const idsToDelete = new Set(promptsToDelete.map((prompt) => prompt.promptId));
        setDraftData((prev) => {
            const actualTopics = prev.filter(({ topicId }) => topicId !== ALL_TOPICS_NAME);
            const updatedTopics = actualTopics.map(({ prompts, ...props }) => ({
                ...props,
                prompts: prompts.filter((prompt) => !idsToDelete.has(prompt.promptId)),
            }));

            return rebuildWithAllTopics(updatedTopics);
        });
    };

    const onEditPrompts = (promptsToMove: IModifiablePrompt[], newTopic: string): void => {
        const idsToMove = new Set(promptsToMove.map((prompt) => prompt.promptId));

        setDraftData((prev) => {
            if (!prev.some(({ name }) => name === newTopic)) {
                return prev;
            }

            const adjustedPrompts = promptsToMove
                .filter((prompt) => prompt.currentTopicName !== newTopic)
                .map((prompt) => ({ ...prompt, currentTopicName: newTopic }));

            return prev.map((topicObj) => {
                const { name, prompts } = topicObj;

                if (name === ALL_TOPICS_NAME) {
                    return {
                        ...topicObj,
                        prompts: prompts.map((p) =>
                            idsToMove.has(p.promptId) ? { ...p, currentTopicName: newTopic } : p,
                        ),
                    };
                }

                if (name === newTopic) {
                    return {
                        ...topicObj,
                        prompts: [...prompts, ...adjustedPrompts],
                    };
                }

                return {
                    ...topicObj,
                    prompts: prompts.filter((p) => !idsToMove.has(p.promptId)),
                };
            });
        });
    };

    const onCreatePrompt = (text: string, topicName: string): IModifiablePrompt => {
        if (draftData.some(({ prompts }) => prompts.some(({ query }) => query.toLowerCase() === text.toLowerCase()))) {
            return null;
        }

        const timestamp = dayjs().toISOString();
        const newPrompt: IModifiablePrompt = {
            topicId: topicName,
            currentTopicName: topicName,
            promptId: `${NEW_PROMPT_ID_SUFFIX}-${text}-${timestamp}`,
            timestamp,
            query: text,
            intent: "",
        };

        setDraftData((prev) => {
            const actualTopics = prev.filter(({ topicId }) => topicId !== ALL_TOPICS_NAME);
            const updatedTopics: IModifiableTopic[] = actualTopics.map((topicObj) => {
                const { name, prompts } = topicObj;

                if (name === topicName) {
                    return {
                        ...topicObj,
                        prompts: [...(prompts || []), newPrompt],
                    };
                }

                return topicObj;
            });

            return rebuildWithAllTopics(updatedTopics);
        });

        return newPrompt;
    };

    const onBatchCreatePrompts = (promptsData: Array<{ text: string; topicName: string }>): IModifiablePrompt[] => {
        if (!promptsData?.length) {
            return [];
        }

        const existingQueries = new Set(
            draftData.flatMap(({ prompts }) => prompts.map(({ query }) => query.toLowerCase())),
        );

        const timestamp = dayjs().toISOString();
        const promptsByTopic = new Map<string, IModifiablePrompt[]>();
        const addedPrompts: IModifiablePrompt[] = [];

        //create all new prompts and group by topic
        promptsData.forEach(({ text, topicName }) => {
            const lowerText = text.toLowerCase();
            if (!existingQueries.has(lowerText)) {
                const newPrompt: IModifiablePrompt = {
                    topicId: topicName,
                    currentTopicName: topicName,
                    promptId: `${NEW_PROMPT_ID_SUFFIX}-${text}-${timestamp}`,
                    timestamp,
                    query: text,
                    intent: "",
                };

                if (!promptsByTopic.has(topicName)) {
                    promptsByTopic.set(topicName, []);
                }

                promptsByTopic.get(topicName)?.push(newPrompt);
                addedPrompts.push(newPrompt);
                existingQueries.add(lowerText);
            }
        });

        //single state update with all prompts
        setDraftData((prev) => {
            const actualTopics = prev.filter(({ topicId }) => topicId !== ALL_TOPICS_NAME);
            const updatedTopics: IModifiableTopic[] = actualTopics.map((topicObj) => {
                const newPromptsForTopic = promptsByTopic.get(topicObj.name) || [];

                if (newPromptsForTopic.length) {
                    return {
                        ...topicObj,
                        prompts: [...(topicObj.prompts || []), ...newPromptsForTopic],
                    };
                }

                return topicObj;
            });

            return rebuildWithAllTopics(updatedTopics);
        });

        return addedPrompts;
    };

    const onCreateTopic = (name: string): void => {
        const trimmedNewName = name.trim();
        if (draftData.some(({ name: topicName }) => topicName.toLowerCase() === trimmedNewName.toLowerCase())) {
            return;
        }

        setDraftData((prev) => [
            ...prev,
            {
                topicId: null,
                name: trimmedNewName,
                prompts: [],
            },
        ]);
    };

    const onGeneratePrompts = async (topic: string, count: number): Promise<void> => {
        if (!canGeneratePrompts) {
            return;
        }

        try {
            const { campaignId, domain, cultureName } = generationProps;
            const response = await generatePrompts({
                campaignId,
                domain,
                cultureName,
                topicName: topic,
                count,
            });

            const newPrompts =
                response?.topics?.flatMap(({ prompts: generatedPrompts }) =>
                    generatedPrompts.map(({ query }) => ({
                        text: query,
                        topicName: topic,
                    })),
                ) ?? [];

            const addedPrompts = onBatchCreatePrompts(newPrompts);

            if (!addedPrompts?.length) {
                dispatch(showErrorToast(translate("llm.prompts.edit.prompts-table.generate.toast.none-added")));
            } else {
                dispatch(
                    showSuccessToast(
                        translate("llm.prompts.edit.prompts-table.generate.toast.success", {
                            count: addedPrompts.length,
                        }),
                    ),
                );
            }
        } catch {
            dispatch(showErrorToast(translate("llm.prompts.edit.prompts-table.modal.server_error")));
        }
    };

    const onEditTopicName = (oldName: string, newName: string): void => {
        const trimmedNewName = newName.trim();
        // Exclude the topic being renamed from the duplicate check; otherwise a
        // casing-only rename collides with itself once both sides are lowercased.
        if (
            draftData.some(
                ({ name: topicName }) =>
                    topicName !== oldName && topicName.toLowerCase() === trimmedNewName.toLowerCase(),
            )
        ) {
            return;
        }

        setDraftData((prev) =>
            prev.map((topicObj) => {
                const { name, prompts } = topicObj;

                switch (name) {
                    case ALL_TOPICS_NAME: {
                        return {
                            ...topicObj,
                            prompts: prompts.map((p) =>
                                p.currentTopicName === oldName
                                    ? {
                                          ...p,
                                          currentTopicName: trimmedNewName,
                                      }
                                    : p,
                            ),
                        };
                    }
                    case oldName: {
                        return {
                            ...topicObj,
                            name: trimmedNewName,
                            prompts: topicObj.prompts.map((prompt) => ({
                                ...prompt,
                                currentTopicName: trimmedNewName,
                            })),
                        };
                    }

                    default:
                        return topicObj;
                }
            }),
        );
    };

    const onDeleteTopic = (name: string): void => {
        setDraftData((prev) => {
            const updatedTopics = prev.filter(
                ({ topicId, name: topicName }) => topicId !== ALL_TOPICS_NAME && topicName !== name,
            );
            return rebuildWithAllTopics(updatedTopics);
        });
    };

    const getUniqueTopicName = (baseName: string): string => {
        const existingTopics = draftData.map(({ name }) => name.toLowerCase());

        if (!existingTopics.some((name) => name === baseName)) {
            return baseName;
        }

        let counter = 1;
        let uniqueName = `${baseName} (${counter})`;

        while (existingTopics.includes(uniqueName.toLowerCase())) {
            counter++;
            uniqueName = `${baseName} (${counter})`;
        }

        return uniqueName;
    };

    const handleCSVUploadSuccess = async (result: ICSVPromptData[]): Promise<number> => {
        return new Promise((resolve) => {
            startTransition(() => {
                const defaultTopicName = getUniqueTopicName(DEFAULT_NEW_TOPIC_NAME as string);
                const processedResult = result.map((entry) => ({
                    ...entry,
                    topic: entry.topic || defaultTopicName,
                }));

                //create all predicted topics for the upcoming prompts, before they're created
                const predictedTopics = new Set(processedResult.map(({ topic }) => topic));
                predictedTopics.forEach((topic) => onCreateTopic(topic));

                //create all prompts in a single batch update
                const actualAddedPrompts = onBatchCreatePrompts(
                    processedResult.map(({ prompt, topic }) => ({
                        text: prompt.toLowerCase(),
                        topicName: topic,
                    })),
                );

                //delete all predicted topics that ended up unused
                const actualAddedTopics = actualAddedPrompts.map(({ currentTopicName: topic }) => topic);
                const unusedTopics = [...predictedTopics].filter((topic) => !actualAddedTopics.includes(topic));
                unusedTopics.forEach((topic) => {
                    const shouldBeDeleted = !draftData.find(({ name, prompts }) => name === topic && prompts.length);

                    if (shouldBeDeleted) {
                        onDeleteTopic(topic);
                    }
                });

                resolve(actualAddedPrompts.length);
            });
        });
    };

    const saveAndClose = (): void => {
        onSave?.(draftData);
        setOpen(false);
    };

    const handleManageLater = (): void => {
        if (onManageLaterClick) {
            onManageLaterClick();
            setOpen(false);
        }
    };

    const { handleUpload: handleCSVUpload, isUploading: isCSVLoading } = useCSVUpload(
        handleCSVUploadSuccess,
        generationProps?.campaignId,
    );
    const { openModal: openCancelModal, modal: cancelChangedModal } = useCancelChangesModal(discardChangesAndClose);
    const { openModal: openEmptyTopicsModal, modal: emptyTopicsModal } = useEmptyTopicsModal(saveAndClose);

    const onApprove = (): void => {
        if (emptyTopics?.length) {
            openEmptyTopicsModal(emptyTopics);
        } else {
            saveAndClose();
        }
    };

    return {
        isOpen,
        openModal,
        modal: (
            <>
                <PromptsEditModal
                    isOpen={isOpen}
                    isLoading={isLoading || isCSVLoading || isGenerating}
                    topics={draftData ?? []}
                    isSaveEnabled={isSaveEnabled}
                    onClose={hasChanges ? openCancelModal : closeModal}
                    onApprove={onApprove}
                    onDeletePrompts={onDeletePrompts}
                    onEditPrompts={onEditPrompts}
                    onCreatePrompt={onCreatePrompt}
                    onCreateTopic={onCreateTopic}
                    onEditTopic={onEditTopicName}
                    onDeleteTopic={onDeleteTopic}
                    onCSVUpload={handleCSVUpload}
                    onGeneratePrompts={canGeneratePrompts ? onGeneratePrompts : undefined}
                    onManageLaterClick={onManageLaterClick ? handleManageLater : undefined}
                    saveText={saveText}
                    error={!!error}
                />
                {cancelChangedModal}
                {emptyTopicsModal}
            </>
        ),
    };
};
